import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { Search } from 'lucide-react';

type OrganizerRequest = { id: number; name: string; contact_email: string; created_at: string; decided_at?: string; status: 'pending' | 'approved' | 'rejected'; admin_message?: string };

export default function AdminOrganizerRequests() {
  const [requests, setRequests] = useState<OrganizerRequest[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [page, setPage] = useState(0);
  const pageSize = 5;
  const [totalCount, setTotalCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [messageDraft, setMessageDraft] = useState<Record<number, string>>({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [confirm, setConfirm] = useState<{ open: boolean; action: 'approve' | 'reject' | null; request: OrganizerRequest | null; submitting: boolean}>({ open: false, action: null, request: null, submitting: false });

  const loadRequests = useCallback(async () => {
    setLoadingList(true);
    try {
      const from = page * pageSize;
      const to = from + pageSize - 1;
      let q = supabase
        .from('organizer_requests')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, to);
      if (statusFilter !== 'all') q = q.eq('status', statusFilter);
      if (searchTerm.trim()) q = q.or(`name.ilike.%${searchTerm.trim()}%,contact_email.ilike.%${searchTerm.trim()}%`);
      const { data, count } = await q;
      setRequests(data || []);
      setTotalCount(count || 0);
    } finally {
      setLoadingList(false);
    }
  }, [page, pageSize, statusFilter, searchTerm]);

  useEffect(() => {
    (async () => {
      const { count } = await supabase
        .from('organizer_requests')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');
      setPendingCount(count || 0);
      const { data: { session } } = await supabase.auth.getSession();
      const uid = session?.user?.id;
      if (uid) {
        const { data: prof } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', uid)
          .maybeSingle();
        setIsAdmin((prof?.role || '') === 'admin');
      }
    })();
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const reviewRequest = async (r: OrganizerRequest, approve: boolean) => {
    try {
      if (!isAdmin) {
        toast.error('Only admins can review organizer requests');
        return;
      }
      const msg = messageDraft[r.id] || '';
      const { error } = await supabase.rpc('review_organizer_request', { request_id: r.id, approve, admin_note: msg });
      if (error) {
        toast.error(error.message || 'Failed to review request');
        return;
      }
      toast.success(approve ? 'Approved' : 'Rejected');
      await loadRequests();
      setMessageDraft(d => { const x = { ...d }; delete x[r.id]; return x; });
      const { count } = await supabase
        .from('organizer_requests')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');
      setPendingCount(count || 0);
    } catch (e: any) {
      toast.error((e?.message as string) || 'Failed to review request');
    }
  };

  return (
    <div id="organizer-requests" className="p-6">
      <div className="w-full max-w-none">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
          <div className="flex flex-col gap-1">
            <h1 className="text-gray-900 dark:text-white text-2xl sm:text-3xl font-black leading-tight tracking-[-0.02em]">Organizer Requests</h1>
            <p className="text-gray-700 dark:text-gray-400 text-base">Review and manage pending organizer requests. {pendingCount} pending.</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 p-4 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 mb-6">
          <label className="relative flex-grow max-w-sm">
            <div className="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-500 dark:text-gray-400">
              <Search className="h-5 w-5" />
            </div>
            <input
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setPage(0); }}
              className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-gray-900 dark:text-white focus:outline-0 focus:ring-2 focus:ring-indigo-600/50 border border-gray-300 dark:border-none bg-white dark:bg-gray-700 h-10 placeholder:text-gray-500 dark:placeholder:text-gray-400 pl-11 pr-4 text-sm"
              placeholder="Search requests..."
              type="text"
            />
          </label>
          <div className="flex items-center gap-2">
            <button onClick={() => { setStatusFilter('pending'); setPage(0); }} className={`px-4 py-2 rounded-lg ${statusFilter==='pending' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-200'}`}>Pending</button>
            <button onClick={() => { setStatusFilter('approved'); setPage(0); }} className={`px-4 py-2 rounded-lg ${statusFilter==='approved' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-200'}`}>Approved</button>
            <button onClick={() => { setStatusFilter('rejected'); setPage(0); }} className={`px-4 py-2 rounded-lg ${statusFilter==='rejected' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-200'}`}>Rejected</button>
            <button onClick={() => { setStatusFilter('all'); setPage(0); }} className={`px-4 py-2 rounded-lg ${statusFilter==='all' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-200'}`}>All</button>
          </div>
        </div>

        <div className="md:hidden space-y-3">
          {loadingList ? (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 text-gray-700 dark:text-gray-400">Loading...</div>
          ) : requests.length === 0 ? (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 text-gray-700 dark:text-gray-400">No requests</div>
          ) : (
            requests.map(r => (
              <div key={r.id} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-full bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-white flex items-center justify-center">
                    {(r.name || r.contact_email || '?').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{r.name || r.contact_email?.split('@')[0]}</p>
                    <p className="text-gray-700 dark:text-gray-400 text-sm">{r.contact_email}</p>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <div className="text-xs text-gray-400">Submitted</div>
                  <div className="text-sm text-gray-700 dark:text-gray-300">{new Date(r.created_at).toLocaleDateString()}</div>
                  <div className="text-xs text-gray-400">Decided</div>
                  <div className="text-sm text-gray-700 dark:text-gray-300">{r.decided_at ? new Date(r.decided_at).toLocaleDateString() : '-'}</div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${r.status==='pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' : r.status==='approved' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'}`}>
                    {r.status==='pending' ? 'Pending Review' : r.status==='approved' ? 'Approved' : 'Rejected'}
                  </span>
                  {r.status === 'pending' && (
                    <div className="flex gap-2">
                      <button
                        disabled={!isAdmin}
                        onClick={() => setConfirm({ open: true, action: 'approve', request: r, submitting: false })}
                        className={`${isAdmin ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 cursor-not-allowed'} px-3 py-1.5 text-xs text-white rounded-lg`}
                      >
                        Approve
                      </button>
                      <button
                        disabled={!isAdmin}
                        onClick={() => setConfirm({ open: true, action: 'reject', request: r, submitting: false })}
                        className={`${isAdmin ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-600 cursor-not-allowed'} px-3 py-1.5 text-xs text-white rounded-lg`}
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </div>
                {r.status === 'pending' && (
                  <div className="mt-2">
                    <input
                      value={messageDraft[r.id] || ''}
                      onChange={e => setMessageDraft(d => ({ ...d, [r.id]: e.target.value }))}
                      placeholder="Message to applicant (optional)"
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white text-sm"
                    />
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <div className="hidden md:block overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr className="text-left">
                  <th className="px-6 py-3 font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Applicant Name</th>
                  <th className="px-6 py-3 font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Email Address</th>
                  <th className="px-6 py-3 font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Date Submitted</th>
                  <th className="px-6 py-3 font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Date Approved</th>
                  <th className="px-6 py-3 font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Message</th>
                  <th className="px-6 py-3 font-medium text-gray-300 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 font-medium text-right text-gray-700 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {loadingList ? (
                  <tr><td className="px-6 py-4 text-gray-700 dark:text-gray-400" colSpan={7}>Loading...</td></tr>
                ) : requests.length === 0 ? (
                  <tr><td className="px-6 py-4 text-gray-700 dark:text-gray-400" colSpan={7}>No requests</td></tr>
                ) : (
                  requests.map(r => (
                    <tr key={r.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="size-10 rounded-full bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-white flex items-center justify-center">
                            {(r.name || r.contact_email || '?').charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium text-gray-900 dark:text-white">{r.name || r.contact_email?.split('@')[0]}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700 dark:text-gray-300">{r.contact_email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700 dark:text-gray-300">{new Date(r.created_at).toLocaleDateString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700 dark:text-gray-300">{r.decided_at ? new Date(r.decided_at).toLocaleDateString() : '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-300">{r.admin_message || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${r.status==='pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' : r.status==='approved' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'}`}>
                          {r.status==='pending' ? 'Pending Review' : r.status==='approved' ? 'Approved' : 'Rejected'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {r.status === 'pending' ? (
                          <>
                            <div className="flex justify-end gap-2">
                              <button
                                disabled={!isAdmin}
                                onClick={() => setConfirm({ open: true, action: 'approve', request: r, submitting: false })}
                                className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${isAdmin ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 cursor-not-allowed'}`}
                              >
                                Approve
                              </button>
                              <button
                                disabled={!isAdmin}
                                onClick={() => setConfirm({ open: true, action: 'reject', request: r, submitting: false })}
                                className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${isAdmin ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-600 cursor-not-allowed'}`}
                              >
                                Reject
                              </button>
                            </div>
                            <div className="mt-2">
                              <input
                                value={messageDraft[r.id] || ''}
                                onChange={e => setMessageDraft(d => ({ ...d, [r.id]: e.target.value }))}
                                placeholder="Message to applicant (optional)"
                                className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white"
                              />
                            </div>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex items-center justify-between p-4 mt-4">
          <p className="text-sm text-gray-700 dark:text-gray-400">Showing {requests.length ? page * pageSize + 1 : 0} to {Math.min((page + 1) * pageSize, totalCount)} of {totalCount}</p>
          <div className="flex items-center gap-2">
            <button disabled={page===0} onClick={() => setPage(p => Math.max(0, p - 1))} className="flex size-10 items-center justify-center text-gray-600 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 disabled:opacity-50">‹</button>
            <button disabled={(page + 1) * pageSize >= totalCount} onClick={() => setPage(p => p + 1)} className="flex size-10 items-center justify-center text-gray-600 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 disabled:opacity-50">›</button>
          </div>
        </div>
      {confirm.open && confirm.request && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{confirm.action === 'approve' ? 'Approve Request' : 'Reject Request'}</h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">Are you sure you want to {confirm.action} the request from {confirm.request.name || confirm.request.contact_email}?</p>
            {messageDraft[confirm.request.id] && (
              <div className="bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-200 rounded p-3 text-sm mb-4">Note: {messageDraft[confirm.request.id]}</div>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirm({ open: false, action: null, request: null, submitting: false })}
                className="px-4 py-2 rounded bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200"
              >
                Cancel
              </button>
              <button
                disabled={confirm.submitting}
                onClick={async () => {
                  setConfirm(c => ({ ...c, submitting: true }));
                  await reviewRequest(confirm.request!, confirm.action === 'approve');
                  setConfirm({ open: false, action: null, request: null, submitting: false });
                }}
                className={`px-4 py-2 rounded text-white ${confirm.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
              >
                {confirm.submitting ? 'Working...' : (confirm.action === 'approve' ? 'Confirm Approve' : 'Confirm Reject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
  );
}
